import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '@noor/lib/cn';

/**
 * The "signature" action menu — the one popup style shared by every row / card
 * context menu in the merchant panel (Figma nodes 24:5869, 24:3019): a white
 * card with a soft ambient shadow, 16px radius and 4px padding, whose items are
 * 12px-radius rows pairing a 20px glyph with a 14px medium label. The
 * destructive item (e.g. "Удалить") tints red.
 *
 * It is a thin, opinionated wrapper over the Radix dropdown-menu primitive so
 * that all action popups look identical — compose it as:
 *
 *   <ActionMenu>
 *     <ActionMenuTrigger asChild><button …/></ActionMenuTrigger>
 *     <ActionMenuContent>
 *       <ActionMenuItem onSelect={…}><MenuEditIcon /> Изменить</ActionMenuItem>
 *       <ActionMenuItem variant="destructive" onSelect={…}>
 *         <MenuTrashIcon /> Удалить
 *       </ActionMenuItem>
 *     </ActionMenuContent>
 *   </ActionMenu>
 */
function ActionMenu(props: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="action-menu" {...props} />;
}

function ActionMenuTrigger(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>,
) {
  return <DropdownMenuPrimitive.Trigger data-slot="action-menu-trigger" {...props} />;
}

function ActionMenuContent({
  className,
  sideOffset = 6,
  align = 'end',
  onClick,
  onKeyDown,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="action-menu-content"
        sideOffset={sideOffset}
        align={align}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.(event);
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          onKeyDown?.(event);
        }}
        className={cn(
          'z-50 min-w-[10.625rem] origin-(--radix-dropdown-menu-content-transform-origin) rounded-2xl bg-popover p-1 text-popover-foreground shadow-lg',
          // Cap the popup at the space Radix measured between the trigger and
          // the viewport edge, and scroll inside it. Without this a long list
          // (a merchant with twenty branches) simply runs off the bottom of
          // the screen: the menu is portalled and positioned, so the page
          // cannot scroll to reveal the rest and those entries are
          // unreachable. `overflow-x-hidden` because a scroll container clips
          // the cross axis anyway — stating it keeps a long label from
          // producing a second, horizontal scrollbar.
          'max-h-(--radix-dropdown-menu-content-available-height) overflow-x-hidden overflow-y-auto',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

function ActionMenuItem({
  className,
  variant = 'default',
  onClick,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  variant?: 'default' | 'destructive';
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="action-menu-item"
      data-variant={variant}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      className={cn(
        "relative flex cursor-pointer items-center gap-2 rounded-xl p-3 text-sm font-medium tracking-tight text-foreground outline-hidden transition-colors select-none focus:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
        'data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:*:[svg]:!text-destructive',
        className,
      )}
      {...props}
    />
  );
}

export { ActionMenu, ActionMenuTrigger, ActionMenuContent, ActionMenuItem };
